/**
 * Pacotes do módulo Estética (Fase 2) — porte do veículo, catálogo padrão e
 * o calendário da garantia. Fonte única para API, app e painel.
 *
 * O pacote NÃO é uma tabela nova: é uma linha de `labor_prices` com
 * `module = 'estetica'` (decisão A7, 24/09). A tabela já é "os serviços com
 * preço da oficina", e a etapa 3 da abertura já lê dela — uma lista paralela
 * seria duas listas de serviço na mesma oficina, a classe-raiz de drift da
 * Regra 11.
 *
 * Sem dependência nenhuma: `@mustauto/utils` importa este arquivo pelo subpath
 * `@mustauto/types/esthetic-packages`, e o build da imagem do site instala só o
 * próprio workspace (ver "O deploy pode falhar no empacotamento", CLAUDE.md).
 */

/** O módulo cujos serviços são pacotes. */
export const PACKAGE_MODULE = 'estetica';

/**
 * Porte do veículo — o eixo que move preço de estética. Um SUV tem mais área
 * de pintura para polir que um hatch, e o detailer orça assim: "polimento
 * R$ 400 no hatch, R$ 550 no SUV". Os modificadores nacional/importado/luxo de
 * `labor_prices` são outro eixo (o da mecânica) e continuam onde estão.
 */
export const VEHICLE_SIZES = ['hatch', 'sedan', 'suv', 'pickup'] as const;
export type VehicleSize = (typeof VEHICLE_SIZES)[number];

export const VEHICLE_SIZE_LABELS: Record<VehicleSize, string> = {
  hatch: 'Hatch',
  sedan: 'Sedã',
  suv: 'SUV',
  pickup: 'Picape',
};

export function isVehicleSize(value: unknown): value is VehicleSize {
  return typeof value === 'string' && (VEHICLE_SIZES as readonly string[]).includes(value);
}

export function vehicleSizeLabel(size: string): string {
  return VEHICLE_SIZE_LABELS[size as VehicleSize] ?? size;
}

/**
 * O porte que o catálogo de veículos sugere pela carroceria
 * (`vehicle_catalog.body_type`). É SUGESTÃO: a abertura mostra o porte já
 * marcado e o mecânico confirma com um toque — a carroceria do catálogo é
 * texto livre e o detailer às vezes cobra a perua como SUV.
 *
 * Perua e cupê vão para sedã (área parecida); van vai para picape, o maior
 * porte. Carroceria desconhecida não sugere nada.
 */
export function vehicleSizeFromBodyType(bodyType: string | null | undefined): VehicleSize | null {
  const key = (bodyType ?? '').trim().toLowerCase();
  switch (key) {
    case 'hatch':
    case 'hatchback':
      return 'hatch';
    case 'sedan':
    case 'seda':
    case 'sedã':
    case 'wagon':
    case 'perua':
    case 'coupe':
    case 'cupe':
    case 'cupê':
      return 'sedan';
    case 'suv':
      return 'suv';
    case 'pickup':
    case 'picape':
    case 'van':
      return 'pickup';
    default:
      return null;
  }
}

/** Preço por porte, em REAIS (como `labor_prices.base_price`). Porte ausente = preço base. */
export type PackageSizePrices = Partial<Record<VehicleSize, number>>;

/** Limites do formulário — o mesmo número para Zod, painel e app. */
export const PACKAGE_DESCRIPTION_MAX = 500;
export const PACKAGE_INCLUDED_ITEM_MAX = 120;
export const PACKAGE_INCLUDED_ITEMS_MAX = 20;
export const PACKAGE_DURATION_HOURS_MAX = 720;
export const PACKAGE_WARRANTY_MONTHS_MAX = 120;

/**
 * Revisões da garantia, em meses depois da conclusão (F2.5). O lembrete sai
 * só para as revisões que cabem na garantia: vitrificação com 12 meses tem as
 * duas; insulfilm com 6, uma; lavagem sem garantia, nenhuma.
 *
 * Duas, e não "a cada 6 meses até o fim": PPF tem garantia de cinco anos, e
 * dez pushes seguidos ensinariam o cliente a desligar a notificação.
 */
export const WARRANTY_REVISION_MONTHS = [6, 12] as const;

export interface DefaultEstheticPackage {
  serviceName: string;
  description: string;
  durationHours: number;
  warrantyMonths: number | null;
  includedItems: string[];
  /** Preço do hatch — o `base_price`, que vale para porte não informado. */
  basePrice: number;
  sizePrices: Required<PackageSizePrices>;
}

/**
 * Catálogo padrão do segmento, semeado na primeira vez que a oficina com o
 * módulo Estética lê a lista (F2.1). **Catálogo vazio na estreia = tela em
 * branco** (armadilha R6): o detailer abriria a primeira OS sem pacote nenhum
 * para escolher.
 *
 * Os preços são pontos de partida de mercado (set/2026) para o gestor ajustar
 * — a tela de Mão de obra avisa isso. Semear é uma vez só: o pacote que o
 * gestor apagar não volta.
 */
export const ESTETICA_DEFAULT_PACKAGES: readonly DefaultEstheticPackage[] = [
  {
    serviceName: 'Lavagem detalhada',
    description: 'Lavagem técnica completa, por fora e por dentro.',
    durationHours: 3,
    warrantyMonths: null,
    includedItems: [
      'Lavagem técnica da carroceria',
      'Rodas e caixas de roda',
      'Aspiração e limpeza interna',
      'Acabamento nos pneus',
    ],
    basePrice: 120,
    sizePrices: { hatch: 120, sedan: 140, suv: 160, pickup: 180 },
  },
  {
    serviceName: 'Descontaminação de pintura',
    description: 'Remove ferro, piche e resíduos que a lavagem não tira.',
    durationHours: 3,
    warrantyMonths: null,
    includedItems: ['Lavagem técnica', 'Descontaminação química (ferro)', 'Clay bar'],
    basePrice: 180,
    sizePrices: { hatch: 180, sedan: 200, suv: 230, pickup: 260 },
  },
  {
    serviceName: 'Polimento técnico — 1 etapa',
    description: 'Lustro: devolve o brilho e tira marcas leves.',
    durationHours: 6,
    warrantyMonths: null,
    includedItems: ['Lavagem e descontaminação', 'Polimento de refino', 'Proteção com cera'],
    basePrice: 350,
    sizePrices: { hatch: 350, sedan: 400, suv: 450, pickup: 500 },
  },
  {
    serviceName: 'Polimento técnico — 2 etapas',
    description: 'Corte e refino: remove riscos médios e manchas.',
    durationHours: 10,
    warrantyMonths: null,
    includedItems: ['Lavagem e descontaminação', 'Polimento de corte', 'Polimento de refino', 'Proteção com selante'],
    basePrice: 600,
    sizePrices: { hatch: 600, sedan: 700, suv: 800, pickup: 900 },
  },
  {
    serviceName: 'Polimento técnico — 3 etapas',
    description: 'Correção completa da pintura, para riscos profundos.',
    durationHours: 16,
    warrantyMonths: null,
    includedItems: [
      'Lavagem e descontaminação',
      'Medição de espessura da pintura',
      'Corte, refino e lustro',
      'Proteção com selante',
    ],
    basePrice: 900,
    sizePrices: { hatch: 900, sedan: 1000, suv: 1150, pickup: 1300 },
  },
  {
    serviceName: 'Vitrificação de pintura',
    description: 'Proteção cerâmica sobre a pintura corrigida.',
    durationHours: 24,
    warrantyMonths: 12,
    includedItems: [
      'Lavagem e descontaminação',
      'Polimento de 1 etapa',
      'Aplicação do vitrificador',
      'Cura',
    ],
    basePrice: 1500,
    sizePrices: { hatch: 1500, sedan: 1700, suv: 1900, pickup: 2100 },
  },
  {
    serviceName: 'PPF — frente',
    description: 'Película de proteção no capô, para-choque e retrovisores.',
    durationHours: 16,
    warrantyMonths: 60,
    includedItems: ['Descontaminação', 'Capô', 'Para-choque dianteiro', 'Retrovisores'],
    basePrice: 3000,
    sizePrices: { hatch: 3000, sedan: 3200, suv: 3500, pickup: 3800 },
  },
  {
    serviceName: 'Insulfilm',
    description: 'Película nos vidros laterais e traseiro.',
    durationHours: 3,
    warrantyMonths: 12,
    includedItems: ['Vidros laterais', 'Vidro traseiro'],
    basePrice: 350,
    sizePrices: { hatch: 350, sedan: 400, suv: 450, pickup: 500 },
  },
  {
    serviceName: 'Higienização interna',
    description: 'Limpeza profunda de bancos, carpetes e teto.',
    durationHours: 5,
    warrantyMonths: null,
    includedItems: ['Bancos', 'Carpetes', 'Teto', 'Painel e portas', 'Oxi-sanitização'],
    basePrice: 250,
    sizePrices: { hatch: 250, sedan: 300, suv: 350, pickup: 400 },
  },
  {
    serviceName: 'Hidratação de couro',
    description: 'Limpeza e hidratação dos bancos de couro.',
    durationHours: 3,
    warrantyMonths: null,
    includedItems: ['Limpeza do couro', 'Hidratação', 'Proteção UV'],
    basePrice: 200,
    sizePrices: { hatch: 200, sedan: 230, suv: 260, pickup: 300 },
  },
];
